import type { Discipline } from "@/domains/pm/types";

/**
 * WHA-3449 — localized full discipline name. English prefers `name_en`
 * (falling back to `description`, then the Estonian `name`); Estonian uses
 * the `name` column. Mirrors the DisciplineBadge tooltip and how
 * disciplines are labelled on the /documents page, so every discipline
 * multi-select reads consistently in the active language.
 */
export function getDisciplineName(d: Discipline, language: string): string {
  if (language === "en") {
    return d.name_en?.trim() || d.description?.trim() || d.name;
  }
  return d.name;
}

/**
 * Localized abbreviation of the discipline itself (VN / VEN). English prefers
 * `abbreviation_en` and falls back to the Estonian code, so a discipline an
 * admin has not given an English code still shows one.
 */
export function getDisciplineAbbreviation(d: Discipline, language: string): string {
  if (language === "en") return d.abbreviation_en?.trim() || d.abbreviation;
  return d.abbreviation;
}

/**
 * Localized code shown on discipline pills: the group code when the
 * discipline belongs to one (KVJ / HVAC), else its own abbreviation. Each
 * English column falls back to its Estonian counterpart.
 */
export function getDisciplineCode(d: Discipline, language: string): string {
  const group = language === "en" ? d.group_code_en?.trim() || d.group_code : d.group_code;
  return group || getDisciplineAbbreviation(d, language);
}

/**
 * Disciplines that share a pill code, in the order each code first appears.
 * Heating, Ventilation and Cooling all read KVJ / HVAC, so drawing one pill
 * per discipline repeats the same code; a list draws one pill per group and
 * names every member in its tooltip instead.
 */
export function groupDisciplinesByCode(
  disciplines: readonly Discipline[],
  language: string
): Discipline[][] {
  const groups = new Map<string, Discipline[]>();
  for (const d of disciplines) {
    const code = getDisciplineCode(d, language);
    const group = groups.get(code);
    if (group) group.push(d);
    else groups.set(code, [d]);
  }
  return Array.from(groups.values());
}
